import React, { useContext, useMemo, useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { ProfileContext } from "layouts/private/ProfileContext";
import HomeCard from "modules/client/home/components/HomeCard";
import { RoutesIcon } from "components/svgs/menu/RoutesIcon";
import AIImportUpsellModal from "../components/AIImportUpsellModal";
import companySettingsApi from "modules/client/settings/api/companySettingsApi";
import { ENTITLEMENT_IDS } from "configs/entitlements";
import "./CreateDeliveryPlanPage.scss";

const deliveryPlanOptions = [
  {
    icon: <RoutesIcon width={40} height={40} fill="#00E2DC" />,
    title: (
      <>
        AI <br />
        Import
      </>
    ),
    description: "Import route data using AI-powered processing.",
    buttonText: "Start AI Import",
    buttonLink: "ai-import",
    requiresEntitlement: true,
    entitlementId: ENTITLEMENT_IDS.AI_IMPORT
  },
  {
    title: (
      <>
        Import <br />
        File
      </>
    ),
    description: "Upload a CSV or Excel file with route data.",
    buttonText: "Upload File",
    buttonLink: "import-file"
  },
  {
    title: (
      <>
        Per <br />
        Package
      </>
    ),
    description: "Create routes by scanning individual packages.",
    buttonText: "Scan Packages",
    buttonLink: "pool-orders/new"
  },
  {
    title: (
      <>
        Manual <br />
        Route
      </>
    ),
    description: "Manually create a route with custom stops.",
    buttonText: "Create Manually",
    buttonLink: "orders/new?action=new"
  },
  {
    title: (
      <>
        Use <br />
        Templates
      </>
    ),
    description: "Start from a saved route template.",
    buttonText: "Coming Soon",
    buttonLink: "saved-routes",
    disabled: true
  },
  {
    title: (
      <>
        Saved <br />
        Plans
      </>
    ),
    description: "View and edit your draft route plans.",
    buttonText: "View Plans",
    buttonLink: "plans"
  }
];

const CreateDeliveryPlanPage = () => {
  const profileCtx = useContext(ProfileContext);
  const profile = useMemo(() => profileCtx.profile, [profileCtx]);
  const history = useHistory();
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [hasAIImportAccess, setHasAIImportAccess] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check AI Import entitlement
  const checkAIImportEntitlement = useCallback(async () => {
    try {
      setCheckingEntitlement(true);
      const data = await companySettingsApi.getEntitlement(ENTITLEMENT_IDS.AI_IMPORT);
      const hasAccess = data?.hasEntitlement === true && data?.isActive === true;
      setHasAIImportAccess(hasAccess);
    } catch (error) {
      console.error("Error checking AI Import entitlement:", error);
      setHasAIImportAccess(false);
    } finally {
      setCheckingEntitlement(false);
    }
  }, []);

  useEffect(() => {
    checkAIImportEntitlement();

    // Listen for entitlement changes
    const handleEntitlementUpdate = () => {
      checkAIImportEntitlement();
    };

    window.addEventListener("entitlementStatusChanged", handleEntitlementUpdate);

    return () => {
      window.removeEventListener("entitlementStatusChanged", handleEntitlementUpdate);
    };
  }, [checkAIImportEntitlement]);

  const handleCardClick = (option) => {
    // If requires entitlement and user doesn't have access, show upsell modal
    if (option.requiresEntitlement && !hasAIImportAccess) {
      setShowUpsellModal(true);
      return;
    }

    // Otherwise navigate normally
    history.push(`/${option.buttonLink}`);
  };

  return (
    <div className="create-delivery-plan-page">
      <div className="delivery-plan-wrapper">
        <div className="greetings-header">
          <div>
            <p className="user-greetings">&#128075; Hi {profile?.firstName}</p>
            <h2 className="sub-greetings">
              How would you like to create your delivery plan?
            </h2>
          </div>
        </div>
        <div className="card-container">
          {deliveryPlanOptions.map((option, index) => {
            // Only disable AI Import card while checking entitlement, not all cards
            const isDisabled =
              option.disabled || (option.requiresEntitlement && checkingEntitlement);

            return (
              <HomeCard
                key={index + 1}
                icon={option.icon}
                title={option.title}
                description={option.description}
                buttonText={option.buttonText}
                buttonLink={option.buttonLink}
                onClick={
                  option.requiresEntitlement ? () => handleCardClick(option) : undefined
                }
                disabled={isDisabled}
              />
            );
          })}
        </div>
      </div>

      <AIImportUpsellModal
        visible={showUpsellModal}
        onCancel={() => setShowUpsellModal(false)}
      />
    </div>
  );
};

export default CreateDeliveryPlanPage;
